  import React, { Component } from "react";
  import { Button, FormGroup, InputGroup, Alert, Intent } from "@blueprintjs/core";
  import { inject, observer } from "mobx-react";
  import RichTextEditor from "react-rte";
  import Toast from "helpers/toaster";
  import * as DataList from "components/DataList";
  import SettingsPage from "components/SettingsPage";
  import FormDrawer from "components/FormDrawer";
  import { FormSection, FieldError } from "components/FormBits";

  // Layout shared by the list and its loading skeleton.
  export const TEMPLATE_COLUMNS = [
    { area: 'name', label: 'Template', width: 'minmax(220px, 1fr)', bone: 'lines' },
    { area: 'actions', label: '', width: '72px', bone: 'none' },
  ];

  export const TEMPLATE_CARDS = {
    columns: 'minmax(0, 1fr) auto',
    areas: ['name actions'],
  };

  export const TEMPLATE_MIN_TABLE_WIDTH = 360;

  // A short plain-text preview of a template's (HTML) scope of work.
  export const previewOf = (html) => {
    if (!html) return '';
    const text = String(html)
      .replace(/<\/li>\s*(?=<li)/gi, ', ') // list items read as a list: "Clean coils, Check refrigerant"
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ')
      .replace(/ ,/g, ',')
      .trim();
    return text.length > 140 ? `${text.slice(0, 137)}…` : text;
  };

  const toolbarConfig = {
    display: ['INLINE_STYLE_BUTTONS', 'BLOCK_TYPE_BUTTONS', 'LINK_BUTTONS', 'BLOCK_TYPE_DROPDOWN', 'HISTORY_BUTTONS'],
    INLINE_STYLE_BUTTONS: [
      {label: 'Bold', style: 'BOLD', className: 'custom-css-class'},
      {label: 'Italic', style: 'ITALIC'}, 
      {label: 'Underline', style: 'UNDERLINE'}
    ],
    BLOCK_TYPE_DROPDOWN: [
      {label: 'Normal', style: 'unstyled'},
      {label: 'Heading Large', style: 'header-one'},
      {label: 'Heading Medium', style: 'header-two'},
      {label: 'Heading Small', style: 'header-three'}
    ],
    BLOCK_TYPE_BUTTONS: [
      {label: 'UL', style: 'unordered-list-item'},
      {label: 'OL', style: 'ordered-list-item'}
    ]
  };

  // Settings > Scope of work templates (/preferences/scope_of_work_templates):
  // the templates list; New / Edit open the template panel.
  @inject('scopeOfWorkTemplateStore')
  @observer
  class ScopeOfWork extends Component {

    state = {
      isDialogOpen: false,
      newTemplateName: '',
      isDeleteAlertOpen: false,
      templateToDelete: null,
      editMode: false,
      selectedScopeOfWork: null,
      name: '',
      nameError: null,
      description: RichTextEditor.createEmptyValue()
    }

    componentDidMount() {
      this.fetchScopeOfWorkTemplates();
    }

    fetchScopeOfWorkTemplates = () => {
      this.props.scopeOfWorkTemplateStore.loadScopeOfWorkTemplates();
    }

    handleDialogOpen = () => {
      this.setState({ 
        isDialogOpen: true,
        nameError: null,
        editMode: false,
        selectedScopeOfWork: null,
        name: '',
        description: RichTextEditor.createEmptyValue()
      });
    }

    handleDialogClose = () => {
      this.setState({ 
        isDialogOpen: false, 
        nameError: null,
        newTemplateName: '',
        editMode: false,
        selectedScopeOfWork: null,
        name: '',
        description: RichTextEditor.createEmptyValue()
      });
    }

    handleNameChange = (e) => {
      if (this.state.editMode) {
        this.setState({ name: e.target.value, nameError: null });
      } else {
        this.setState({ newTemplateName: e.target.value, nameError: null });
      }
    }

    handleDescriptionChange = (value) => {
      this.setState({ description: value });
    }

    handleSubmit = () => {
      const { scopeOfWorkTemplateStore } = this.props;
      const { editMode, selectedScopeOfWork, name, newTemplateName, description } = this.state;

      // The name is required: say so under the field and put the cursor there.
      if (!(editMode ? name || '' : newTemplateName).trim()) {
        this.setState({ nameError: 'Give the template a name, e.g. Furnace replacement.' }, () => {
          const field = document.getElementById('template-name');
          if (field) field.focus();
        });
        return;
      }

      if (editMode && selectedScopeOfWork) {
        scopeOfWorkTemplateStore.updateScopeOfWorkTemplate(selectedScopeOfWork.id, {
          name: name,
          description: description.toString('html')
        }).then(() => {
          this.handleDialogClose();
          Toast.show({ message: 'Template saved.', intent: Intent.SUCCESS });
        }).catch(() => {
          Toast.show({ message: "Couldn't save the template. Try again.", intent: Intent.DANGER });
        });
      } else if (newTemplateName.trim()) {
        scopeOfWorkTemplateStore.createScopeOfWorkTemplate(newTemplateName, description.toString('html'))
          .then(() => {
            // Stay on the list (the store reloads it); the new template is in it.
            this.handleDialogClose();
            Toast.show({ message: 'Template created.', intent: Intent.SUCCESS });
          })
          .catch(() => {
            Toast.show({ message: "Couldn't create the template. Try again.", intent: Intent.DANGER });
          });
      }
    }

    // templateToDelete is the whole template, so the prompt can name it.
    handleDeleteClick = (template) => {
      this.setState({ 
        isDeleteAlertOpen: true,
        templateToDelete: template
      });
    }

    handleDeleteConfirm = () => {
      const { scopeOfWorkTemplateStore } = this.props;
      const { templateToDelete } = this.state;
      this.setState({ isDeleteAlertOpen: false, templateToDelete: null });
      scopeOfWorkTemplateStore.deleteScopeOfWorkTemplate(templateToDelete.id)
        .then(() => {
          Toast.show({ message: `${templateToDelete.attributes.name} deleted.`, intent: Intent.SUCCESS });
        })
        .catch(() => {
          Toast.show({ message: `Couldn't delete ${templateToDelete.attributes.name}. Try again.`, intent: Intent.DANGER });
        });
    }

    handleDeleteCancel = () => {
      this.setState({ 
        isDeleteAlertOpen: false,
        templateToDelete: null
      });
    }

    handleEditClick = (scopeOfWork) => {
      this.setState({
        isDialogOpen: true,
        nameError: null,
        editMode: true,
        selectedScopeOfWork: scopeOfWork,
        name: scopeOfWork.attributes.name,
        description: scopeOfWork.attributes.description ? 
          RichTextEditor.createValueFromString(scopeOfWork.attributes.description, 'html') :
          RichTextEditor.createEmptyValue()
      });
    }

    renderList() {
      const { objects, isLoading, isError } = this.props.scopeOfWorkTemplateStore.scopeOfWorkTemplates;
      const templates = Array.isArray(objects) ? objects : [];
      if (isLoading && templates.length === 0) {
        return <DataList.Loading columns={TEMPLATE_COLUMNS} cards={TEMPLATE_CARDS} minTableWidth={TEMPLATE_MIN_TABLE_WIDTH} noun="templates" rows={4} />;
      }
      if (isError) {
        return <DataList.LoadError noFilters noun="templates" onRetry={this.fetchScopeOfWorkTemplates} />;
      }
      if (templates.length === 0) {
        return (
          <DataList.Empty
            noun="templates"
            title="No templates yet"
            message="Save the scope of work you write often, like a furnace replacement, and drop it into estimates."
            extraAction={{ label: 'New template', icon: 'add', onClick: this.handleDialogOpen, primary: true }}
          />
        );
      }
      return (
        <DataList.List
          columns={TEMPLATE_COLUMNS}
          cards={TEMPLATE_CARDS}
          minTableWidth={TEMPLATE_MIN_TABLE_WIDTH}
          total={templates.length}
          noun="templates"
        >
          {templates.map((template) => (
            <DataList.Row key={template.id}>
              <DataList.Cell area="name">
                <DataList.Lines primary={template.attributes.name} secondary={previewOf(template.attributes.description)} />
              </DataList.Cell>
              <DataList.Cell area="actions">
                <DataList.Actions
                  items={[
                    { icon: 'edit', label: `Edit ${template.attributes.name}`, onClick: () => this.handleEditClick(template) },
                    { icon: 'trash', label: `Delete ${template.attributes.name}`, intent: Intent.DANGER, onClick: () => this.handleDeleteClick(template) },
                  ]}
                />
              </DataList.Cell>
            </DataList.Row>
          ))}
        </DataList.List>
      );
    }

    render() {
      const { editMode, templateToDelete } = this.state;
      return (
        <SettingsPage
          title="Scope of work templates"
          description="Scope of work text you use often, ready to drop into estimates."
          actions={<Button className="sp-button sp-button--primary" icon="add" text="New template" onClick={this.handleDialogOpen} />}
        >
          {this.renderList()}

          <FormDrawer
            isOpen={this.state.isDialogOpen}
            onClose={this.handleDialogClose}
            title={editMode ? "Edit template" : "New template"}
            icon={editMode ? "edit" : "document"}
            size="min(760px, 100vw)"
            footer={
              <>
                <Button text="Cancel" onClick={this.handleDialogClose} />
                <Button
                  intent={Intent.PRIMARY}
                  icon={editMode ? "floppy-disk" : "add"}
                  text={editMode ? "Save changes" : "Create template"}
                  onClick={this.handleSubmit}
                />
              </>
            }
          >
            <FormSection title="Template" hint="Reusable scope of work text to drop into estimates.">
              <FormGroup
                className={this.state.nameError ? "has-error" : undefined}
                label="Name"
                labelFor="template-name"
                labelInfo="*"
              >
                <InputGroup
                  id="template-name"
                  placeholder="e.g. Furnace replacement"
                  value={editMode ? this.state.name : this.state.newTemplateName}
                  onChange={this.handleNameChange}
                  aria-invalid={!!this.state.nameError}
                />
                <FieldError>{this.state.nameError}</FieldError>
              </FormGroup>
              <FormGroup
                label="Scope of work"
                helperText={editMode ? undefined : "Once it’s created, the template opens so you can finish it."}
              >
                <RichTextEditor
                  value={this.state.description}
                  onChange={this.handleDescriptionChange}
                  toolbarConfig={toolbarConfig}
                  placeholder="Describe the work, e.g. Remove the old furnace, install the new unit, test and haul away."
                />
              </FormGroup>
            </FormSection>
          </FormDrawer>

          <Alert
            isOpen={this.state.isDeleteAlertOpen}
            confirmButtonText="Delete template"
            cancelButtonText="Cancel"
            intent={Intent.DANGER}
            icon="trash"
            onConfirm={this.handleDeleteConfirm}
            onCancel={this.handleDeleteCancel}
          >
            <strong>{templateToDelete ? `Delete ${templateToDelete.attributes.name}?` : 'Delete this template?'}</strong>
            <p>It won't be offered when writing estimates. This can't be undone.</p>
          </Alert>
        </SettingsPage>
      );
    }
  }

  export default ScopeOfWork;
